import { Box, Heading, IconButton, Link, Stack, Text, useToast } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { HiOutlineArrowDownTray, HiOutlineDocument } from 'react-icons/hi2';

import { trpc } from '../../config/trpc';
import { useCurrentDraft } from '../../hooks/useCurrentDraft';
import { Accordion, AccordionBody, AccordionHeader } from '../Accordion';
import { AccordionButton } from '../AccordionButton';
import { LabeledText } from '../LabeledText';
import { Draft } from './types';

/**
 * Deep link to the original message in the ingest mailbox, by RFC822 Message-ID — the road back to the full thread,
 * headers and anything the attachment filter turned away.
 */
const gmailSearchUrl = (messageId?: string | null): string | undefined =>
  messageId
    ? `https://mail.google.com/mail/u/0/#search/${encodeURIComponent(`rfc822msgid:${messageId.replace(/^<|>$/g, '')}`)}`
    : undefined;

/** One attachment the draft kept: the same signed-URL download the request's Documents panel uses. */
const AttachmentRow = ({ document }: { document: Draft['documents'][number] }) => {
  const { mutateAsync: downloadDocument, isLoading } = trpc.downloadDocument.useMutation();
  const toast = useToast({ position: 'bottom', duration: 4000 });

  const handleDownload = async () => {
    try {
      const { signedUrl } = await downloadDocument({ id: document.id });
      window.open(signedUrl, '_blank');
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  };

  return (
    <Stack direction="row" align="center" spacing="2">
      <HiOutlineDocument />
      <Text fontSize="sm" noOfLines={1} flex="1" title={document.original_filename}>
        {document.original_filename}
      </Text>
      <IconButton
        icon={<HiOutlineArrowDownTray />}
        aria-label={`Download ${document.original_filename}`}
        variant="outline"
        size="xs"
        isLoading={isLoading}
        onClick={handleDownload}
      />
    </Stack>
  );
};

/** The inbound email a draft was extracted from: sender, subject, collapsible body, and the attachments it kept. */
export const SourceEmailCard = () => {
  const { draft } = useCurrentDraft();
  const documents = draft?.documents ?? [];

  return (
    <Accordion isOpen>
      <AccordionHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Heading size="sm">Source Email</Heading>
          <Stack direction="row" alignItems="center" spacing="3">
            <Show if={draft?.source === 'imap' && !!draft?.message_id}>
              <Link
                href={gmailSearchUrl(draft?.message_id)}
                isExternal
                fontSize="sm"
                textDecoration="underline"
                onClick={(event) => event.stopPropagation()}
              >
                Open in Gmail
              </Link>
            </Show>
            <AccordionButton />
          </Stack>
        </Stack>
      </AccordionHeader>
      <AccordionBody>
        <Stack spacing="1" mt="1">
          <LabeledText label="From" value={draft?.email_from ?? '-'} isHorizontal />
          <LabeledText label="Subject" value={draft?.email_subject ?? '-'} isHorizontal />
          <Box borderWidth="1px" borderRadius="md" p="3" maxH="72" overflowY="auto" whiteSpace="pre-wrap" fontSize="sm">
            {draft?.email_text ?? ''}
          </Box>
          <Show if={documents.length > 0}>
            <Stack spacing="1" pt="2">
              <Text fontSize="xs" fontWeight="bold" color="gray.600">
                Attachments
              </Text>
              {documents.map((document) => (
                <AttachmentRow key={document.id} document={document} />
              ))}
            </Stack>
          </Show>
        </Stack>
      </AccordionBody>
    </Accordion>
  );
};
